import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
  Typography,
  Divider,
  IconButton,
  Alert,
} from "@mui/material";
import { Deal } from "../../../validation/customer.validation";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

export interface Banker {
  id: string;
  name?: string;
  workEmail?: string;
  email?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  isImportedFromExcel?: boolean;
  user?: {
    id: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
  };
  maxLTV?: number | string;
  minDSCRatio?: number | string;
  [key: string]: any;
}

interface AssignedBankerDialogProps {
  open: boolean;
  deal: Deal | null;
  bankers: Banker[];
  onClose: () => void;
  onSave: (
    dealId: string,
    bankerIds: string[],
    importedBankerIds: string[],
    isShareCustomerDetails: boolean,
  ) => Promise<void>;
  saving?: boolean;
}

const parseNumber = (v: any) => {
  if (v === null || v === undefined || v === "") return NaN;
  return Number(String(v).replace(/,/g, ""));
};

const getBankerId = (banker: Banker) =>
  banker.isImportedFromExcel ? banker.id : banker.user?.id || banker.id;

const getBankerLabel = (banker: Banker) => {
  const user = banker.user || banker;
  return (
    `${user.firstName || ""} ${user.middleName || ""} ${user.lastName || ""}`
      .trim() ||
    banker.name ||
    banker.workEmail ||
    banker.email ||
    banker.id
  );
};

const getBankerEmail = (banker: Banker) =>
  banker.email || banker.workEmail || banker.user?.email || "";

const AssignedBankerDialog: React.FC<AssignedBankerDialogProps> = ({
  open,
  deal,
  bankers,
  onClose,
  onSave,
  saving,
}) => {
  const [selected, setSelected] = useState<Banker[]>([]);

  const bankersById = useMemo(() => {
    return bankers.reduce((acc, b) => {
      acc[getBankerId(b)] = b;
      return acc;
    }, {} as Record<string, Banker>);
  }, [bankers]);

  const eligibleBankers = useMemo(() => {
    if (!deal) return bankers;
    const dealMaxLTV = parseNumber(deal.maxLTV);
    const dealMinDSR = parseNumber(deal.minDSCRatio);
    return bankers.filter((b) => {
      const bMax = parseNumber(b.maxLTV);
      const bMinDsr = parseNumber(b.minDSCRatio);
      const okLtv = !isNaN(bMax) ? bMax >= dealMaxLTV : true;
      const okDsr = !isNaN(bMinDsr) ? bMinDsr <= dealMinDSR : true;
      return okLtv && okDsr;
    });
  }, [bankers, deal]);

  useEffect(() => {
    if (!deal) {
      setSelected([]);
      return;
    }
    
    const assignedIds: string[] = (deal.assignedBanks || [])
      .map((item: any) =>
        typeof item === "string" ? item : item?.bankId || item?.bank?.id,
      )
      .filter(Boolean);
    
    const assignedObjs = assignedIds
      .map((id: string) => bankersById[id])
      .filter(Boolean);

    const importedAssignedObjs = bankers.filter(
      (banker) =>
        banker.isImportedFromExcel &&
        Array.isArray(banker.dealIds) &&
        banker.dealIds.includes(deal.id),
    );
      
    setSelected([...assignedObjs, ...importedAssignedObjs]);
  }, [deal, bankers, bankersById]);

  const handleSave = (isShareCustomerDetails: boolean) => {
    if (!deal) return;
    const bankerIds = selected
      .filter((banker) => !banker.isImportedFromExcel)
      .map(getBankerId);
    const importedBankerIds = selected
      .filter((banker) => banker.isImportedFromExcel)
      .map(getBankerId);

    onSave(deal.id as string, bankerIds, importedBankerIds, isShareCustomerDetails);
  };

  const noEligible = eligibleBankers.length === 0;
  const selectedImportedWithoutEmail = selected.filter(
    (banker) => banker.isImportedFromExcel && !getBankerEmail(banker),
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: "#f5f5f5",
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          <PersonSearchIcon color="primary" />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Assign Bankers
          </Typography>
          <Typography
            variant="body1"
            component="span"
            color="text.secondary"
          >
            {deal ? `- ${deal.loanName}` : ""}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: "primary.main", alignSelf: { xs: 'flex-end', sm: 'center' } }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ mb: 2, borderRadius: 1, backgroundColor: "info.lightest" }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            Showing bankers that match deal requirements:
            <Box component="span" sx={{ fontWeight: 700, mx: 0.5 }}>
              Max LTV: {deal?.maxLTV ?? "Any"}
            </Box>
            and
            <Box component="span" sx={{ fontWeight: 700, mx: 0.5 }}>
              Min DSCR: {deal?.minDSCRatio ?? "Any"}.
            </Box>
          </Typography>
        </Box>

        {noEligible ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography
              variant="body1"
              color="error.main"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              No Eligible Bankers Found!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No bankers currently match the deal's Max LTV and Min DSCR
              requirements. You may need to adjust the deal constraints or
              consult the full list of bankers outside this tool.
            </Typography>
          </Box>
        ) : (
          <>
            {selectedImportedWithoutEmail.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Email not found for {selectedImportedWithoutEmail.map(getBankerLabel).join(", ")}.
              </Alert>
            )}
          <Autocomplete
            multiple
            options={eligibleBankers}
            value={selected}
            getOptionLabel={getBankerLabel}
            onChange={(_, value) => setSelected(value)}
            renderTags={(value: Banker[], getTagProps) =>
              value.map((option: Banker, index: number) => {
                const { key, ...chipProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={getBankerLabel(option)}
                    size="small"
                    variant="filled"
                    color={option.isImportedFromExcel ? "secondary" : "primary"}
                    {...chipProps}
                  />
                );
              })
            }
            renderOption={(props, option) => {
              const label = getBankerLabel(option);
              const meta = `LTV: ${option.maxLTV ?? "-"} | MinDSR: ${
                option.minDSCRatio ?? "-"
              }`;
              const email = getBankerEmail(option);
              return (
                <li {...props} key={option.id}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", py: 0.5, width: "100%" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Box sx={{ fontWeight: 600 }}>{label}</Box>
                      {option.isImportedFromExcel && (
                        <Chip
                          label="Imported from Excel"
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Box sx={{ fontSize: 12, color: email ? "text.secondary" : "error.main" }}>
                      {email || "Email not found"}
                    </Box>
                    <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                      {option.isImportedFromExcel ? "Invitation will be sent on assign" : meta}
                    </Box>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Bankers"
                placeholder="Search and select bankers"
                size="small"
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: 45, // 🔹 Increase height here
                    alignItems: "flex-start", // ensures text aligns properly
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                  "& .MuiChip-root": {
                    height: 26, // optional - slightly larger chips
                  },
                }}
              />
            )}
            disabled={!deal}
          />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          variant="outlined"
          color="error"
          sx={{
            borderRadius: 1,
            fontWeight: 600,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {handleSave(false)}}
          variant="contained"
          disabled={saving || !deal || selected.length === 0}
          startIcon={
            saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            borderRadius: 1,
            fontWeight: 600,
            minWidth: { xs: '100%', sm: 150 },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {saving ? "Saving..." : "Assign"}
        </Button>
        <Button
          onClick={() => {handleSave(true)}}
          variant="contained"
          color="secondary"
          disabled={saving || !deal || selected.length === 0}
          startIcon={
            saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          sx={{
            borderRadius: 1,
            fontWeight: 600,
            minWidth: { xs: '100%', sm: 150 },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {saving ? "Saving..." : "Assign With Customer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignedBankerDialog;
